import type React from "react";
import type { AnnotationLabelOption } from "./annotationLabels";
import type { AnnotationEntityLike, AnnotationTextSegment, SpannedAnnotationEntity } from "./annotationSpans";

/**
 * Layout of annotation labels over the text: lanes, label widths and abbreviations, and
 * their colours. Browser font metrics are measured on a canvas; character counts are only
 * the fallback before measuring.
 */
export const ANNOTATION_LANE_GAP_REM = 1;
export const ANNOTATION_LANE_OFFSET_REM = 2.05;
export const ANNOTATION_LANE_PADDING_REM = 0.75;
/** A label tag's text size: readable at a glance (O-1 R1-03; it was 9 px), and with its padding
 * (about 15 px) still within one lane's gap. */
export const ANNOTATION_LABEL_FONT_PX = 11;
/** How far a label tag sits above its bar's line, so the tag is centred on the bar. */
export const ANNOTATION_LABEL_RISE_REM = 0.62;
const ANNOTATION_RENDER_CHUNK_PATTERN = /\S+[^\S\r\n]*|[^\S\r\n]+|[\r\n]+/g;
const LABEL_BAR_VISIBLE_GUTTER = "0.2rem";
const LABEL_BAR_VISIBLE_GUTTER_PX = 3;
let annotationMeasureCanvas: HTMLCanvasElement | null = null;

type AnnotationColorStyle = React.CSSProperties & {
  "--tw-ring-color"?: string;
};

/** A label that starts with its reference number, "#9 benefice valuation", shortens to that number: its initials
 * ("#BV") can't tell two of a kind apart (usability round 5). The whole label shows on hover. */
const NUMBERED_LABEL = /^#\d+/;

function getAnnotationLabelAbbreviation(label: string, value: string, length?: number): string {
  const numbered = NUMBERED_LABEL.exec(label);
  if (numbered !== null) return numbered[0];
  const abbreviationLength =
    length ?? Math.max(1, Math.min(4, value.trim().length));
  const labelParts = label
    .replace(/[()]/g, " ")
    .split(/[\s_-]+/)
    .filter(Boolean);

  if (labelParts.length > 1) {
    const initials = labelParts
      .map((part) => part[0])
      .join("")
      .toUpperCase();

    return initials.slice(0, Math.min(abbreviationLength, initials.length));
  }

  return label.slice(0, abbreviationLength);
}

function getLongestMeasuredLabelCandidate(
  label: string,
  value: string,
  sourceFont: string,
  labelFont: string
): string | null {
  const spanText = value.trim();
  if (!spanText || !sourceFont || !labelFont) {
    return null;
  }

  const availableWidth = Math.max(
    getTextWidth(spanText, sourceFont) - LABEL_BAR_VISIBLE_GUTTER_PX,
    0
  );
  if (getTextWidth(label, labelFont) <= availableWidth) {
    return label;
  }

  const fallbackLength = Math.max(1, Math.min(4, spanText.length));
  for (let length = fallbackLength; length > 0; length -= 1) {
    const candidate = getAnnotationLabelAbbreviation(label, value, length);
    if (candidate && getTextWidth(candidate, labelFont) <= availableWidth) {
      return candidate;
    }
  }

  return getAnnotationLabelAbbreviation(label, value, 1);
}

/**
 * shortens the label name to fit the visible annotation span. Browser font metrics are preferred; character count is only the pre-measurement fallback.
 */
export function getAnnotationLabelDisplayName(
  type: string,
  value: string,
  sourceFont = "",
  labelFont = ""
): string {
  const label = type.trim() || "Annotation";
  const measuredCandidate = getLongestMeasuredLabelCandidate(
    label,
    value,
    sourceFont,
    labelFont
  );
  if (measuredCandidate) {
    return measuredCandidate;
  }

  const spanTextLength = value.trim().length;
  if (spanTextLength === 0 || label.length <= spanTextLength) {
    return label;
  }

  return getAnnotationLabelAbbreviation(label, value);
}

export function getAnnotationLabelMaxWidth(value: string, font: string): string {
  const spanText = value.trim();
  if (!spanText) {
    return "0px";
  }

  if (!font) {
    return `max(0px, calc(${spanText.length}ch - ${LABEL_BAR_VISIBLE_GUTTER}))`;
  }

  return `max(0px, calc(${getTextWidth(spanText, font).toFixed(2)}px - ${LABEL_BAR_VISIBLE_GUTTER}))`;
}

// convenience function for splitting text segments into smaller chunks for rendering, so that long segments don't cause issues with label placement
export function splitAnnotationTextSegments<T extends AnnotationEntityLike>(
  segments: Array<AnnotationTextSegment<T>>
): Array<AnnotationTextSegment<T>> {
  return segments.flatMap((segment) => {
    const chunks: Array<AnnotationTextSegment<T>> = [];
    ANNOTATION_RENDER_CHUNK_PATTERN.lastIndex = 0;
    let match = ANNOTATION_RENDER_CHUNK_PATTERN.exec(segment.text);

    while (match) {
      const chunkText = match[0];
      const start = segment.start + match.index;

      chunks.push({
        ...segment,
        start,
        end: start + chunkText.length,
        text: chunkText,
      });

      match = ANNOTATION_RENDER_CHUNK_PATTERN.exec(segment.text);
    }

    return chunks.length > 0 ? chunks : segment;
  });
}

/**
 * Calculates the width of the given text using a canvas context with the specified font. This way the actual length of the text is measured in pixels instead of just by the number of characters. If no font is provided, the function returns the length of the text as a fallback.
 */
function getTextWidth(text: string, font: string): number {
  if (!font) {
    return text.length;
  }

  annotationMeasureCanvas = annotationMeasureCanvas || document.createElement("canvas");
  const context = annotationMeasureCanvas.getContext("2d");
  if (!context) {
    return text.length;
  }

  context.font = font;
  return context.measureText(text).width;
}

/**
 * 
 * @param text the input text(span) for which the visual center offset should be calculated
 * @param font the font used to render the text
 * @returns the approximately calculated offset (in characters) of the visual center of the text, which can be used to position labels or other elements relative to the text span. Note: This is an approximation and may not be pixel-perfect, especially for complex scripts or fonts.
 */
export function getVisualCenterOffset(text: string, font: string): number {
  const targetWidth = getTextWidth(text, font) / 2;
  let bestOffset = 0;
  let bestDistance = Number.POSITIVE_INFINITY;

  for (let offset = 0; offset <= text.length; offset += 1) {
    const width = getTextWidth(text.slice(0, offset), font);
    const distance = Math.abs(width - targetWidth);
    if (distance < bestDistance) {
      bestDistance = distance;
      bestOffset = offset;
    }
  }

  return bestOffset;
}

/**
 * returns the percentage position of the anchor point within the given annotation text segment, based on the provided anchor offset and font. This way the label gets positioned at the visual center of the span instead of just the character center which is usually off due to punctaion or whitespace
 */
export function getAnnotationLabelAnchorPercent<T extends AnnotationEntityLike>(
  annotation: SpannedAnnotationEntity<T>,
  segment: AnnotationTextSegment<T>,
  anchorOffset: number,
  font: string
): number | null {
  const absoluteAnchor = annotation.start + anchorOffset;
  if (
    segment.start === segment.end ||
    absoluteAnchor < segment.start ||
    absoluteAnchor > segment.end ||
    (absoluteAnchor === segment.start && absoluteAnchor !== annotation.start)
  ) {
    return null;
  }

  const localAnchor = Math.min(
    Math.max(absoluteAnchor - segment.start, 0),
    segment.text.length
  );
  const segmentWidth = getTextWidth(segment.text, font);
  if (segmentWidth <= 0) {
    return ((absoluteAnchor - segment.start) / (segment.end - segment.start)) * 100;
  }

  return (getTextWidth(segment.text.slice(0, localAnchor), font) / segmentWidth) * 100;
}

export function getTextOffsetWithinContainer(
  container: HTMLElement,
  targetNode: Node,
  targetOffset: number
): number | null {
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  let offset = 0;
  let currentNode = walker.nextNode();

  while (currentNode) {
    const parentElement = currentNode.parentElement;
    const isSourceText = Boolean(parentElement?.closest("[data-annotation-source='true']"));
    if (!isSourceText) {
      currentNode = walker.nextNode();
      continue;
    }

    if (currentNode === targetNode) {
      return offset + targetOffset;
    }

    offset += currentNode.textContent?.length || 0;
    currentNode = walker.nextNode();
  }

  return null;
}

export function getAnnotationLabelColorStyle(
  labelOption: AnnotationLabelOption
): AnnotationColorStyle {
  return {
    backgroundColor: labelOption.backgroundColor,
    borderColor: labelOption.borderColor,
    color: labelOption.textColor,
    "--tw-ring-color": labelOption.ringColor,
  };
}
